// Google Consent Mode v2 defaults + region/GPC helpers.
//
// The default snippet below runs BEFORE GTM / GA4 / Meta Pixel / Google Ads via a
// beforeInteractive <Script> in app/layout.js, so the four consent signals
// (ad_storage / ad_user_data / ad_personalization / analytics_storage) have a
// state set before any tag can fire. Regions where prior opt-in is required by
// law get `denied` defaults; every other region is granted implicitly (no region
// override -> the tag does not treat consent as denied).
//
// The CMP (components/CookieConsentBanner) reads the same region list and helper
// functions here so it never drifts from the defaults set by this snippet.

// Opt-in required (defaults = denied). Countries where advertising / analytics
// cookies legally require prior consent before setting. Kept in one place so the
// gtag default region list and the CMP show/hide decision use the SAME source.
//
//   EEA-27 + UK + Iceland + Liechtenstein + Norway + Switzerland
//     -> GDPR Art. 6 / ePrivacy Art. 5(3) / UK PECR / Swiss FADP.
//   BR    -> LGPD + ANPD guidance (2024/2025).
//
// Quebec (Law 25) also requires opt-in for profiling but Google's consent-mode
// `region` parameter takes ISO country codes / country-subdivision codes; we
// don't detect Canadian province server-side, so Quebec is handled client-side
// in the CMP via the `fr-CA` browser language proxy (imperfect but the pragmatic
// default; a Quebec English-speaker gets the granted default + minimal notice).
export const CONSENT_OPT_IN_COUNTRIES = [
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE',
  'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
  'GB','IS','LI','NO','CH','BR',
]

// US states that require honoring the Global Privacy Control (GPC) header /
// navigator.globalPrivacyControl signal as a valid opt-out of sale/share of
// personal information. Enforced by state AGs (Sephora $1.2M CA, 2022;
// CA/CO/CT joint sweep 2025). We honor GPC regardless of location detection
// (a GPC-enabled browser is treated as opt-out anywhere), but this list is the
// definitive citation of WHY. Not currently used to gate behavior — it is
// documentation for the compliance decision. Update as more states pass laws.
export const GPC_HONORING_US_STATES = [
  'CA','CO','CT','DE','MD','MN','MT','NE','NH','NJ','OR','TX',
]

// The default consent snippet. Runs beforeInteractive so the four consent
// signals have a state set before ANY tag can fire.
//
// Sequence in a single synchronous script (Option C — GDPR spec + no timing
// gap):
//
//   1. gtag('consent', 'default', {denied, region:[EEA/UK/CH/BR list]})
//      Always. Regardless of stored cookie. Google's IP geo scopes the denied
//      to visitors physically in the opt-in list; everyone else is implicit
//      granted. This is what a GDPR audit expects to see as the initial
//      default state on EVERY page load, including returning-visitor loads.
//
//   2. If vrs_cc_cookie exists AND stores accepted categories, IMMEDIATELY
//      (same script, same tick) fire gtag('consent', 'update', {...matching
//      stored categories...}). Because the update is inline right after the
//      default — before any next/script tag has a chance to run — there is no
//      timing window where a queued tag reads the stale denied state.
//
// This satisfies both Google Consent Mode v2 spec (defaults are always the
// region-denied baseline; updates carry the user's actual choice) AND the
// returning-visitor race concern (tags never see denied when the visitor has
// already accepted). Jahangir flagged the previous single-branch defaults as
// non-spec 2026-08-05 — this addresses that.
//
// Advanced Consent Mode: tags load and send cookieless "consent pings"
// pre-consent so Google's modeling can recover attribution once consent is
// granted — vs Basic mode which loads no tag at all (loses ~30-50% of
// measurable conversions).
//
// Kept synchronous and inline: this MUST run before any next/script tag hits
// the page, so no async fetch, no import, no promise.
export const CONSENT_DEFAULT_SNIPPET = `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments)}
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
    region: ${JSON.stringify(CONSENT_OPT_IN_COUNTRIES)}
  });
  gtag('set', 'ads_data_redaction', true);
  (function(){
    try {
      var m = document.cookie.match(/(?:^|;\\s*)vrs_cc_cookie=([^;]+)/);
      if (!m) return;
      var stored = JSON.parse(decodeURIComponent(m[1]));
      if (!stored || !Array.isArray(stored.categories)) return;
      var cats = stored.categories;
      var analytics = cats.indexOf('analytics') !== -1 ? 'granted' : 'denied';
      var marketing = cats.indexOf('marketing') !== -1 ? 'granted' : 'denied';
      gtag('consent', 'update', {
        ad_storage: marketing,
        ad_user_data: marketing,
        ad_personalization: marketing,
        analytics_storage: analytics
      });
    } catch (e) { /* malformed cookie -> keep the region-denied default */ }
  })();
`

const OPT_IN_SET = new Set(CONSENT_OPT_IN_COUNTRIES)

// Read the two-letter shipping country cookie the site's own geolocation sets
// on first visit (utils/geolocation + region overlay). Uppercased.
function readShippingCountryCookie() {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(/(?:^|;\s*)shippingCountry=([A-Za-z]{2})/)
  return m ? m[1].toUpperCase() : null
}

// Best-effort language-tag country hint (navigator.language "en-GB" -> "GB").
// Only used as a fallback when the shippingCountry cookie is not yet set.
function readLanguageCountry() {
  if (typeof navigator === 'undefined') return null
  const tag = (navigator.language || '').toUpperCase()
  const cc = tag.split('-')[1]
  return cc && /^[A-Z]{2}$/.test(cc) ? cc : null
}

// Is this visitor in an opt-in-required jurisdiction? Uses the same signals
// (shippingCountry cookie first, browser language fallback) the rest of the
// site uses. Also treats `fr-CA` as opt-in (Quebec Law 25 proxy) even though
// the shippingCountry cookie only stores CA at the country level.
export function isOptInRequired() {
  if (typeof window === 'undefined') return false
  const country = readShippingCountryCookie()
  if (country) {
    if (OPT_IN_SET.has(country)) return true
    // Quebec proxy: shippingCountry=CA + fr-CA browser language.
    if (country === 'CA') {
      const tag = (navigator.language || '').toLowerCase()
      if (tag === 'fr-ca' || tag.startsWith('fr-ca')) return true
    }
    return false
  }
  // No cookie yet: fall back to browser language.
  const langCountry = readLanguageCountry()
  if (langCountry && OPT_IN_SET.has(langCountry)) return true
  const langTag = (navigator.language || '').toLowerCase()
  if (langTag === 'fr-ca' || langTag.startsWith('fr-ca')) return true
  return false
}

// Does the browser advertise Global Privacy Control? Universally honored (not
// gated on region) — a GPC-enabled visitor from anywhere is treated as opt-out
// of ads-related storage. Analytics storage stays available (GPC is a
// "do-not-sell/share" signal, not an analytics-opt-out).
export function hasGpcSignal() {
  if (typeof navigator === 'undefined') return false
  return navigator.globalPrivacyControl === true
}

// Translate the CMP's category list into the four GCM v2 consent signals.
// `analytics` -> analytics_storage; `marketing` -> ads triplet.
export function categoriesToConsentPatch(categories) {
  const cats = Array.isArray(categories) ? categories : []
  const analytics = cats.includes('analytics') ? 'granted' : 'denied'
  const marketing = cats.includes('marketing') ? 'granted' : 'denied'
  return {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  }
}

// True iff the visitor has accepted the marketing category (Meta Pixel /
// Google Ads / anything that puts user data toward ad targeting). Reads the
// vrs_cc_cookie the CMP writes. Falls back to `false` when the cookie is
// absent (first-time visitor pre-consent) OR malformed — the compliance-safe
// default. Runs client-side only; SSR always returns false.
//
// Kept exported for any client-side code that needs to gate on marketing
// consent directly. GTM now handles the Meta/GA4/Ads pixel gating via
// Advanced Consent Mode.
export function hasMarketingConsent() {
  if (typeof document === 'undefined') return false
  try {
    const m = document.cookie.match(/(?:^|;\s*)vrs_cc_cookie=([^;]+)/)
    if (!m) return false
    const stored = JSON.parse(decodeURIComponent(m[1]))
    return Array.isArray(stored.categories) && stored.categories.includes('marketing')
  } catch (_e) {
    return false
  }
}
